import type { GuidedSetupStep } from '../components/guided-setup/guided-setup-steps';

/**
 * Guided tours for `/risks/container` and `/risks/sca`.
 *
 * Both pages render `app-finding-table`, which carries the SAME four `data-tour-step` anchors
 * (`scope-selector`, `refresh`, `export-actions`, `findings-table` —
 * `finding-table.component.html`) regardless of which page hosts it. The two tours below spotlight
 * the SAME anchors but with different step text, because the segmented `scope-selector` group
 * means something different on each page (finding sub-type on Container, component provenance on
 * SCA) — that is the reason for two separate step arrays instead of one shared set.
 *
 * Each page owns its OWN seen-flag key so seeing one tour never suppresses the other
 * (`.claude/rules/new-section-onboarding-coverage.md` R2).
 *
 * ## The `scope-selector` step is flag-gated, and so is the bucket-count nuance
 *
 * The whole U6/U7 scope surface — the segmented selector AND the per-bucket grouping level it
 * ungates — is keyed off the `ciIngestEnabled` deployment capability (`phx.sca.ci-ingest-enabled`,
 * exposed by `GET /api/features`; `FindingTableComponent.showScopeSelector`). It was a default-OFF
 * `sbomCiScopeFilters` flag until the 2026-09-21 audit. Before 2026-09-03 these tours relied on the
 * overlay's absent-anchor skip for that, which worked but recorded nothing: a four-step tour
 * silently became a three-step tour, and the two facts that are only true with the flag ON (the
 * selector exists; a bucket row's count is an ASSET count, not a finding count) were carried by a
 * step that vanished and by a step that did not. Both now live on the one `requiresFlag`-tagged
 * step, so with the flag off the tour is an honest three-step tour of the surface that is actually
 * rendered — and turning the flag on adds exactly the step that explains what appeared.
 */

/** Legacy per-BROWSER seen key for the /risks/container tour, kept only for the Phase-2 seed. */
export const RISKS_CONTAINER_TOUR_STORAGE_KEY = 'phx.risksContainer.guidedSetupSeen';

/** Legacy per-BROWSER seen key for the /risks/sca tour, kept only for the Phase-2 seed. */
export const RISKS_SCA_TOUR_STORAGE_KEY = 'phx.risksSca.guidedSetupSeen';

/**
 * `/risks/container` tour. The segmented `scope-selector` group here filters by FINDING SUB-TYPE
 * (misconfiguration vs. image CVE) — see `risks-container.ts`'s `CONTAINER_SCOPE_OPTIONS`.
 */
export const RISKS_CONTAINER_TOUR_STEPS: readonly GuidedSetupStep[] = [
  {
    id: 'scope-selector',
    title: 'Filter by finding type',
    anchor: 'scope-selector',
    requiresFlag: 'ciIngestEnabled',
    body: [
      '"All" shows every container finding. "Image CVEs" narrows to CVE-identified vulnerabilities ' +
        'found in the image; "Misconfigurations" is everything else — Dockerfile and image-build ' +
        'issues, but also Kubernetes-manifest and Compose findings.',
      'This selector works the same way in every grouping mode below (Tree, File, Flat) — it no ' +
        'longer forces the table into a flat list to apply the filter.',
      'It also adds a bucket level to the grouped table. A bucket row\u2019s count is the number ' +
        'of distinct assets in that bucket — a different metric from the finding count on the ' +
        'repository row above it, so the two do not add up and are not meant to.',
    ],
  },
  {
    id: 'refresh',
    title: 'Refresh',
    anchor: 'refresh',
    body: ['Re-fetches the current filter and grouping from the server without changing either.'],
  },
  {
    id: 'export-actions',
    title: 'Export or send findings',
    anchor: 'export-actions',
    body: [
      'Download the currently filtered findings as a Phoenix import JSON file, or send them ' +
        'directly to Phoenix Security from here.',
    ],
  },
  {
    id: 'findings-table',
    title: 'The findings table',
    anchor: 'findings-table',
    body: [
      'Container findings for this scope, grouped by workspace/repository, file, or as a flat ' +
        'list depending on the "Group by" control above.',
    ],
  },
];

/**
 * `/risks/sca` tour. The segmented `scope-selector` group here filters by COMPONENT PROVENANCE
 * (which of a component's locations to show) — see `risks-sca.ts`'s `SCA_SCOPE_OPTIONS`.
 */
export const RISKS_SCA_TOUR_STEPS: readonly GuidedSetupStep[] = [
  {
    id: 'scope-selector',
    title: 'Filter by component provenance',
    anchor: 'scope-selector',
    requiresFlag: 'ciIngestEnabled',
    body: [
      'These filter by component PROVENANCE — which of a component\'s locations to show. ' +
        '"Combined" shows every SCA finding regardless of provenance. "Build" narrows to ' +
        'components resolved from your build manifests; "Container" narrows to components found ' +
        'inside a built container image.',
      'This selector works the same way in every grouping mode below (Tree, File, Flat) — it no ' +
        'longer forces the table into a flat list to apply the filter.',
      'It also adds a bucket level to the grouped table. A bucket row\u2019s count is the number ' +
        'of distinct assets in that bucket — a different metric from the finding count on the ' +
        'repository row above it, so the two do not add up and are not meant to.',
    ],
  },
  {
    id: 'refresh',
    title: 'Refresh',
    anchor: 'refresh',
    body: ['Re-fetches the current filter and grouping from the server without changing either.'],
  },
  {
    id: 'export-actions',
    title: 'Export or send findings',
    anchor: 'export-actions',
    body: [
      'Download the currently filtered findings as a Phoenix import JSON file, or send them ' +
        'directly to Phoenix Security from here.',
    ],
  },
  {
    id: 'findings-table',
    title: 'The findings table',
    anchor: 'findings-table',
    body: [
      'SCA findings for this scope, one row per CVE per component location, grouped by ' +
        'workspace/repository, file, or as a flat list depending on the "Group by" control above.',
    ],
  },
];
